import React, { useState } from "react";
import classes from "../Management.module.scss";
import styles from "./DealerPipeline.module.scss";
import { requestDealerDeposit, clearDealerDepositRequest, markDealerDepositReceived, formatUsd } from "../../../services/dealerGarage";
import { formatDate, errorMessage, parseMoney, ageLabel, todayYmd } from "./dealerAdmin";

// How the deposit arrived (depositTransactions DEPOSIT_METHODS).
const METHODS = [
  ["quickbooks", "QuickBooks"],
  ["shopify", "Shopify"],
  ["wire", "Wire transfer"],
  ["ach", "ACH"],
  ["check", "Check"],
  ["cash", "Cash"],
  ["card", "Card"],
  ["other", "Other"],
];

/**
 * The deposit, from the lead or inspection: "Ask for a deposit" once it has
 * been talked through (logs the QuickBooks deposit invoice sent, which with
 * its number also lands in the customer's billing), and "Mark received" when
 * the money arrives. Received money goes into the customer's deposit balance
 * exactly as recording it in Billing does - paying that QuickBooks invoice
 * when there is one - and an inspection waiting on it is accepted.
 *
 * Every customer is tracked the same way: one without an account gets a guest
 * account for the invoice or the money, and a guest account gets no emails.
 *
 * A customer who already has money in their balance (recorded in Billing, or
 * on another lead) is shown as covered rather than asked again: recording it
 * here too would count it twice. More can still be asked for or recorded.
 *
 * Props: target ({ inspectionId } | { inquiryId }), request (depositRequest),
 *        account ({ _id, isGuest } | null), depositBalance (the account's),
 *        suggestedAmount, onChanged(depositRequest, data) - data is the API's
 *        (account, status)
 */
const DepositRequestBox = ({ target, request, account, depositBalance = 0, suggestedAmount, onChanged }) => {
  const [form, setForm] = useState(null);
  const [received, setReceived] = useState(null);
  const [busy, setBusy] = useState(false);
  const r = request || {};
  const isInspection = !!target.inspectionId;
  const onFile = depositBalance > 0 ? formatUsd(depositBalance, { cents: false }) : "";

  const open = () => setForm({ amount: suggestedAmount ? String(suggestedAmount) : "", externalRef: "", payUrl: "" });

  // With money already on file, the amount is left blank so the deposit they
  // already paid is not typed in again.
  const openReceived = () => setReceived({
    amount: r.amount ? String(r.amount) : (suggestedAmount && !onFile ? String(suggestedAmount) : ""),
    date: todayYmd(),
    method: "quickbooks",
    reference: "",
  });

  const submit = async () => {
    const amount = parseMoney(form.amount);
    if (!amount || Number.isNaN(amount) || amount <= 0) {
      alert("Enter the deposit amount.");
      return;
    }
    setBusy(true);
    try {
      const res = await requestDealerDeposit({ ...target, amount, externalRef: form.externalRef.trim(), payUrl: form.payUrl.trim() });
      if (res?.data && res.data.depositRequest) {
        setForm(null);
        onChanged(res.data.depositRequest, res.data);
        // Recorded, but the invoice could not be (no account could be opened).
        if (!res.data.invoice && form.externalRef.trim()) alert(res.message);
      } else {
        alert(errorMessage(res, "Could not record the deposit request."));
      }
    } catch (err) {
      alert("Could not record the deposit request.");
    } finally {
      setBusy(false);
    }
  };

  const submitReceived = async () => {
    const amount = parseMoney(received.amount);
    if (!amount || Number.isNaN(amount) || amount <= 0) {
      alert("Enter the amount received.");
      return;
    }
    if (!received.date) {
      alert("Enter the date it arrived.");
      return;
    }
    setBusy(true);
    try {
      const res = await markDealerDepositReceived({
        ...target,
        amount,
        date: received.date,
        method: received.method,
        reference: received.reference.trim(),
      });
      if (res?.data && res.data.depositRequest) {
        setReceived(null);
        onChanged(res.data.depositRequest, res.data);
      } else {
        alert(errorMessage(res, "Could not record the deposit."));
      }
    } catch (err) {
      alert("Could not record the deposit.");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Take the deposit request off? Any QuickBooks invoice it recorded stays - void that separately.")) return;
    setBusy(true);
    const res = await clearDealerDepositRequest(target).catch(() => null);
    setBusy(false);
    if (res?.data) onChanged(res.data.depositRequest, res.data);
    else alert(errorMessage(res, "Could not clear it."));
  };

  if (r.paidAt) {
    return (
      <div className={styles.inlineRow}>
        <span className={`${styles.chip} ${styles.chipGood}`}>Deposit received {formatUsd(r.amount, { cents: false })}</span>
        <span className={styles.hint}>{formatDate(r.paidAt)} · in their deposit balance (corrections in Billing)</span>
      </div>
    );
  }
  if (received) {
    return (
      <div className={styles.subForm}>
        <div className={styles.grid3}>
          <div className={classes.formGroup}>
            <label className={classes.formLabel} htmlFor="dep-recv-amount">Amount received (USD)</label>
            <input id="dep-recv-amount" className={classes.formInput} value={received.amount} placeholder="e.g. 2000" onChange={(e) => setReceived((f) => ({ ...f, amount: e.target.value }))} />
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel} htmlFor="dep-recv-date">Date received</label>
            <input id="dep-recv-date" type="date" className={classes.formInput} value={received.date} max={todayYmd()} onChange={(e) => setReceived((f) => ({ ...f, date: e.target.value }))} />
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel} htmlFor="dep-recv-method">Paid by</label>
            <select id="dep-recv-method" className={classes.formInput} value={received.method} onChange={(e) => setReceived((f) => ({ ...f, method: e.target.value }))}>
              {METHODS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
        </div>
        <div className={classes.formGroup}>
          <label className={classes.formLabel} htmlFor="dep-recv-ref">Reference</label>
          <input id="dep-recv-ref" className={classes.formInput} value={received.reference} placeholder="QuickBooks payment or Shopify order # (optional)" onChange={(e) => setReceived((f) => ({ ...f, reference: e.target.value }))} />
        </div>
        <p className={styles.hint}>
          {!account
            ? "They have no account yet, so a guest account is opened for them and the money goes into its deposit balance."
            : onFile
              ? `Adds to the ${onFile} already in their deposit balance, the same as recording it in Billing.`
              : "Goes into their deposit balance, the same as recording it in Billing."}
          {r.invoiceId && r.externalRef ? ` It pays QuickBooks invoice #${r.externalRef}.` : ""}
          {isInspection ? " An inspection waiting on it is accepted." : ""}
          {!account || account.isGuest
            ? " Guest accounts get no emails."
            : (isInspection ? " No receipt is emailed; if the inspection is accepted they're told it's confirmed." : " No receipt is emailed.")}
        </p>
        <div className={classes.formButtons} style={{ alignItems: "center" }}>
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={submitReceived}>
            {busy ? "Saving..." : "Record deposit received"}
          </button>
          <button type="button" className={styles.btnText} disabled={busy} onClick={() => setReceived(null)}>Cancel</button>
        </div>
      </div>
    );
  }
  if (r.requestedAt) {
    return (
      <div className={styles.inlineRow}>
        <span className={`${styles.chip} ${styles.chipWarn}`}>Deposit requested {formatUsd(r.amount, { cents: false })} · {ageLabel(r.requestedAt)}</span>
        {r.externalRef && <span className={styles.hint}>QuickBooks #{r.externalRef}</span>}
        <button type="button" className={styles.btn} disabled={busy} onClick={openReceived}>Mark received</button>
        <button type="button" className={styles.btnText} disabled={busy} onClick={clear}>Clear</button>
      </div>
    );
  }
  if (!form && onFile) {
    return (
      <div className={styles.inlineRow}>
        <span className={`${styles.chip} ${styles.chipGood}`}>Deposit on file {onFile}</span>
        <span className={styles.hint}>Already in their balance, so there's nothing to record here.</span>
        <button type="button" className={styles.btnText} onClick={open}>Ask for more</button>
        <button type="button" className={styles.btnText} onClick={openReceived}>Record more</button>
      </div>
    );
  }
  if (!form) {
    return (
      <div className={styles.inlineRow}>
        <button type="button" className={styles.btn} onClick={open}>Ask for a deposit</button>
        <button type="button" className={styles.btn} onClick={openReceived}>Mark received</button>
        <span className={styles.hint}>Ask once you've talked it through; already paid? Mark it received.</span>
      </div>
    );
  }
  return (
    <div className={styles.subForm}>
      <div className={styles.grid3}>
        <div className={classes.formGroup}>
          <label className={classes.formLabel} htmlFor="dep-amount">Amount (USD)</label>
          <input id="dep-amount" className={classes.formInput} value={form.amount} placeholder="e.g. 2000" onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
        </div>
        <div className={classes.formGroup}>
          <label className={classes.formLabel} htmlFor="dep-ref">QuickBooks invoice #</label>
          <input id="dep-ref" className={classes.formInput} value={form.externalRef} placeholder="optional" onChange={(e) => setForm((f) => ({ ...f, externalRef: e.target.value }))} />
        </div>
        <div className={classes.formGroup}>
          <label className={classes.formLabel} htmlFor="dep-pay">Pay link</label>
          <input id="dep-pay" className={classes.formInput} value={form.payUrl} placeholder="https://… (optional)" onChange={(e) => setForm((f) => ({ ...f, payUrl: e.target.value }))} />
        </div>
      </div>
      <p className={styles.hint}>
        {suggestedAmount ? `Suggested: 10% of the car (${formatUsd(suggestedAmount, { cents: false })}). ` : ""}
        With the QuickBooks number, the invoice and its pay link also show in their billing
        {account ? "." : " - a guest account is opened for them."}
        {isInspection ? " When the payment is recorded, the inspection is accepted automatically." : ""}
      </p>
      <div className={classes.formButtons} style={{ alignItems: "center" }}>
        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={submit}>
          {busy ? "Saving..." : "Mark deposit requested"}
        </button>
        <button type="button" className={styles.btnText} disabled={busy} onClick={() => setForm(null)}>Cancel</button>
      </div>
    </div>
  );
};

export default DepositRequestBox;
